/**
 * MachineScheduleLegend.tsx
 * Color legend for the Machine Schedule grid (Phase 2, Item 4).
 *
 * Matches the mockup legend:
 *   Available | In Use / Work Order | Maintenance | Calibration | Out of Service
 *
 * Rendered at the bottom of the Machine Schedule page, below the grid.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ConflictBadgeShape, type ConflictType } from '../../../components/schedule/ConflictBadge';

interface LegendEntry {
  label: string;
  color: string;
  /** If true, render a hatched pattern instead of a solid color box */
  hatched?: boolean;
  /** If true, render a dashed border + dimmed fill (mirrors the "reserved in
   *  another plan" bar so the swatch isn't confused with the other grey rows). */
  dashed?: boolean;
  /** Distinct border colour (Run Mode Phase C: continuous bars keep their
   *  status fill but carry a heavier border in the continuous token). */
  borderColor?: string;
  /** When set, render the matching conflict-badge SHAPE (triangle=hard, dot=soft)
   *  so the key matches what the bars draw — not a colour-only swatch. */
  conflictShape?: ConflictType;
}

const LegendSwatch: React.FC<{ color: string; hatched?: boolean; dashed?: boolean; borderColor?: string }> = ({ color, hatched, dashed, borderColor }) => {
  const swatchStyle: React.CSSProperties = {
    width: 20,
    height: 14,
    borderRadius: 3,
    border: borderColor
      ? `2px solid ${borderColor}`
      : `1px ${dashed ? 'dashed' : 'solid'} ${alpha(color, 0.8)}`,
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
    opacity: dashed ? 0.5 : 1,
  };

  const hatchStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundImage: [
      'repeating-linear-gradient(',
      `  45deg,`,
      `  ${alpha(color, 0.6)} 0px,`,
      `  ${alpha(color, 0.6)} 3px,`,
      `  ${alpha(color, 0.15)} 3px,`,
      `  ${alpha(color, 0.15)} 6px`,
      ')',
    ].join(''),
  };

  return (
    <Box sx={swatchStyle}>
      {hatched ? <Box sx={hatchStyle} /> : <Box sx={{ backgroundColor: color, height: '100%' }} />}
    </Box>
  );
};

interface MachineScheduleLegendProps {
  /**
   * Show the "Reserved in another plan" swatch (show-the-blocker). Only the
   * scenario Machine grid surfaces other-draft tentative bars, so this entry is
   * opt-in — the live Machine Schedule page leaves it off (back-compat).
   */
  showOtherPlan?: boolean;
}

export const MachineScheduleLegend: React.FC<MachineScheduleLegendProps> = ({
  showOtherPlan = false,
}) => {
  const theme = useTheme();

  const entries: LegendEntry[] = [
    { label: 'Available', color: theme.schedule.noActivity },
    { label: 'Work Order (Committed)', color: theme.schedule.committed },
    { label: 'Quote / Tentative', color: theme.schedule.tentative, hatched: true },
    // Run Mode Phase C — continuous bars keep their status fill but carry the
    // heavier continuous border + a 24/7 badge, and run THROUGH weekends.
    { label: 'Continuous (runs 24/7 incl. weekends)', color: theme.schedule.committed, borderColor: theme.schedule.continuous },
    { label: 'Maintenance', color: theme.schedule.maintenance },
    { label: 'Calibration', color: alpha(theme.schedule.maintenance, 0.7) },
    { label: 'Out of Service', color: theme.schedule.noActivity },
    { label: 'Hard Conflict (overlap)', color: theme.schedule.conflictHard, conflictShape: 'hard' },
    { label: 'Soft Conflict (capability)', color: theme.schedule.conflictSoft, conflictShape: 'soft' },
    // Item 6.1 — the INDEX row is a separate capacity currency (tests/day, not
    // minutes): a daily % heatmap, not booking bars. Same green/yellow/red
    // band language as the rest of the legend (theme.schedule.heatmap*).
    { label: 'Index tests (% of daily capacity)', color: theme.schedule.heatmapLow },
    ...(showOtherPlan
      ? [{ label: 'Reserved in another plan', color: theme.schedule.noActivity, dashed: true }]
      : []),
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        mt: 2,
        px: 1.5,
        py: 1,
        borderTop: `1px solid ${theme.border.color}`,
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ mr: 1, alignSelf: 'center', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6rem' }}
      >
        Legend
      </Typography>
      {entries.map((e) => (
        <Box key={e.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {e.conflictShape ? (
            <ConflictBadgeShape
              type={e.conflictShape}
              color={e.color}
              ringColor={theme.palette.background.paper}
            />
          ) : (
            <LegendSwatch color={e.color} hatched={e.hatched} dashed={e.dashed} borderColor={e.borderColor} />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            {e.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
